import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ParticleBackground from '../ParticleBackground/ParticleBackground';
import RelatedPages from './RelatedPages';
import { PUBLIC_FOOTER_LINKS, PUBLIC_HEADER_LINKS } from './publicNav';

const text = ({ theme }: { theme?: { colors?: { text?: string } } }) =>
  theme?.colors?.text ?? '#344767';

const line = ({ theme }: { theme?: { colors?: { cardBorder?: string } } }) =>
  theme?.colors?.cardBorder ?? 'rgba(0, 0, 0, 0.08)';

const Page = styled.div`
  position: relative;
  z-index: 1;
  min-height: 100vh;
  background: transparent;
  color: ${text};
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${line};
`;

const Brand = styled(Link)`
  color: ${text};
  font-weight: 700;
  font-size: 1.15rem;
  text-decoration: none;
  letter-spacing: 0.02em;
`;

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.25rem;
  align-items: center;
`;

const NavLink = styled(Link)`
  color: ${text};
  text-decoration: none;
  font-size: 0.95rem;
  opacity: 0.85;

  &:hover {
    opacity: 1;
  }
`;

const SignInLink = styled(Link)`
  color: ${text};
  text-decoration: none;
  border: 1px solid ${line};
  border-radius: 0.5rem;
  padding: 0.45rem 0.8rem;
  font-size: 0.95rem;
`;

const Main = styled.main`
  width: min(880px, 100%);
  margin: 0 auto;
  padding: 3rem 1.5rem 4rem;
  flex: 1;
`;

const FooterBar = styled.footer`
  border-top: 1px solid ${line};
  padding: 1.5rem;
  color: ${text};
  font-size: 0.9rem;
  opacity: 0.85;
`;

const FooterNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  margin-bottom: 0.75rem;
`;

const FooterLink = styled(Link)`
  color: ${text};
`;

type PublicSiteLayoutProps = {
  children: React.ReactNode;
};

const PublicSiteLayout = ({ children }: PublicSiteLayoutProps): JSX.Element => {
  return (
    <Page>
      <ParticleBackground />
      <Header>
        <Brand to="/">Hesychia</Brand>
        <Nav aria-label="Primary">
          {PUBLIC_HEADER_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to}>
              {link.label}
            </NavLink>
          ))}
          <SignInLink to="/signin/">Sign in</SignInLink>
        </Nav>
      </Header>
      <Main>
        {children}
        <RelatedPages />
      </Main>
      <FooterBar>
        <FooterNav aria-label="Footer">
          {PUBLIC_FOOTER_LINKS.map((link) => (
            <FooterLink key={link.to} to={link.to}>
              {link.label}
            </FooterLink>
          ))}
        </FooterNav>
        <div>
          © 2026 Hesychia · Developed by{' '}
          <a href="https://www.aimloperations.com">AI ML Operations, LLC</a>
        </div>
      </FooterBar>
    </Page>
  );
};

export default PublicSiteLayout;
